import { Button } from "@/components/ui/button";
import { 
  Leaf, 
  ShieldCheck, 
  Heart, 
  Droplets, 
  Baby, 
  Dog,
  Sparkles,
  AlertTriangle,
  X,
  Check,
  ArrowRight,
  Home
} from "lucide-react";
import { Link } from "react-router-dom";

const comparisonData = [
  {
    feature: "Ingredients",
    plantora: "Plant-derived, natural extracts",
    chemical: "Harsh synthetic chemicals",
    plantoraIcon: Leaf,
    chemicalIcon: AlertTriangle
  },
  {
    feature: "Skin Safety",
    plantora: "Gentle & soft on hands",
    chemical: "Can cause irritation & dryness",
    plantoraIcon: Heart,
    chemicalIcon: AlertTriangle
  },
  {
    feature: "Child Safety",
    plantora: "Non-toxic, safe around kids",
    chemical: "Harmful if ingested or touched",
    plantoraIcon: Baby,
    chemicalIcon: AlertTriangle
  },
  {
    feature: "Pet Friendly",
    plantora: "Safe for pets",
    chemical: "Toxic to animals",
    plantoraIcon: Dog,
    chemicalIcon: AlertTriangle
  },
  {
    feature: "Environment",
    plantora: "Biodegradable, eco-friendly",
    chemical: "Pollutes water & soil",
    plantoraIcon: Droplets,
    chemicalIcon: AlertTriangle
  },
  {
    feature: "Cleaning Power",
    plantora: "Effective plant-powered formula",
    chemical: "Effective but at a cost",
    plantoraIcon: Sparkles,
    chemicalIcon: ShieldCheck
  },
  {
    feature: "Fragrance",
    plantora: "Fresh, natural scent",
    chemical: "Artificial, overpowering smell",
    plantoraIcon: Leaf,
    chemicalIcon: AlertTriangle
  },
  {
    feature: "Residue",
    plantora: "No harmful residue",
    chemical: "Leaves chemical residue",
    plantoraIcon: Check,
    chemicalIcon: X
  }
];

const benefits = [
  {
    icon: Leaf,
    title: "Plant-Based Ingredients",
    description: "Our formulas are crafted with natural plant extracts, ensuring a clean that's powered by nature."
  },
  {
    icon: Baby,
    title: "Safe for Little Ones",
    description: "Non-toxic ingredients mean you never have to worry about your children's safety around cleaned surfaces."
  },
  {
    icon: Dog,
    title: "Pet Approved",
    description: "Your furry friends can roam freely without exposure to harmful chemicals that traditional cleaners leave behind."
  },
  {
    icon: Heart,
    title: "Gentle on Skin",
    description: "No more dry, irritated hands. Our plant-based formula is as gentle as nature intended."
  },
  {
    icon: Droplets,
    title: "Eco-Friendly",
    description: "Biodegradable ingredients that won't harm our waterways, soil, or the planet we call home."
  },
  {
    icon: Sparkles,
    title: "Powerful Clean",
    description: "Don't mistake gentle for weak. Our plant-powered formula delivers sparkling results every time."
  }
];

export default function WhyPlantoraPage() {
  return (
    <main className="min-h-screen">
      {/* Header */}
      <header className="bg-forest py-4 px-6 md:px-12 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-sage transition-colors">
            <Home className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
          <Link to="/" data-testid="why-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_3cbc8c81-fb19-4dcb-8665-22a7d3f951d4/artifacts/tql0rd5s_6cf00645-7ffc-4b87-89b5-7aecf16c3cb9.jpeg" 
              alt="Plantora Logo" 
              className="h-16 md:h-20 w-auto object-contain"
            />
          </Link>
          <div className="w-24" />
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 md:py-28 px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sage via-sage-light to-white" />
        <div className="absolute inset-0 botanical-pattern opacity-30" />
        
        <div className="relative max-w-4xl mx-auto text-center">
          <span className="inline-block text-sm font-medium text-forest bg-white/80 px-4 py-2 rounded-full mb-6">
            The Plantora Difference
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-forest leading-tight mb-6">
            Why Choose Plant-Based
            <br />
            <span className="text-forest-light">Over Chemical Cleaners?</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Traditional cleaners may get the job done, but at what cost? Discover why millions 
            are making the switch to plant-powered cleaning solutions.
          </p>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-white" data-testid="comparison-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-forest mb-4">
              Side-by-Side Comparison
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              See how Plantora stacks up against traditional chemical cleaners
            </p>
          </div>

          {/* Comparison Table */}
          <div className="bg-stone-50 rounded-3xl overflow-hidden shadow-lg">
            {/* Table Header */}
            <div className="grid grid-cols-3 bg-forest text-white">
              <div className="p-4 md:p-6 font-semibold text-center border-r border-white/20">
                Feature
              </div>
              <div className="p-4 md:p-6 font-semibold text-center border-r border-white/20 flex items-center justify-center gap-2">
                <Leaf className="w-5 h-5 text-citrus" />
                Plantora
              </div>
              <div className="p-4 md:p-6 font-semibold text-center flex items-center justify-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-300" />
                Chemical Cleaners
              </div>
            </div>

            {/* Table Body */}
            {comparisonData.map((item, index) => (
              <div 
                key={index}
                className={`grid grid-cols-3 ${index % 2 === 0 ? 'bg-white' : 'bg-stone-50'} ${index !== comparisonData.length - 1 ? 'border-b border-stone-200' : ''}`}
              >
                <div className="p-4 md:p-5 font-medium text-forest border-r border-stone-200 flex items-center">
                  {item.feature}
                </div>
                <div className="p-4 md:p-5 text-forest border-r border-stone-200">
                  <div className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base">{item.plantora}</span>
                  </div>
                </div>
                <div className="p-4 md:p-5 text-gray-600">
                  <div className="flex items-start gap-2">
                    <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base">{item.chemical}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Grid Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sage/30 via-white to-sage/20" />
        <div className="absolute top-20 right-10 w-96 h-96 bg-citrus/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-forest/5 rounded-full blur-3xl" />

        <div className="relative max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-forest mb-4">
              The Plantora Advantage
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Experience cleaning that cares for your family and the planet
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-shadow duration-300"
                data-testid={`benefit-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-sage flex items-center justify-center mb-4">
                  <benefit.icon className="w-7 h-7 text-forest" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-forest mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Chemical Dangers Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-red-50" data-testid="dangers-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full mb-4">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Important to Know</span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Hidden Dangers of Chemical Cleaners
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 border-l-4 border-red-400">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Respiratory Issues</h3>
              <p className="text-gray-600 text-sm">
                Harsh chemical fumes can trigger asthma, allergies, and long-term respiratory problems, especially in children.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border-l-4 border-red-400">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Skin Irritation</h3>
              <p className="text-gray-600 text-sm">
                Prolonged exposure to chemical cleaners can cause dermatitis, burns, and chronic skin conditions.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border-l-4 border-red-400">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Environmental Damage</h3>
              <p className="text-gray-600 text-sm">
                Chemical runoff pollutes our water systems, harms marine life, and contaminates soil for generations.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border-l-4 border-red-400">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Pet Toxicity</h3>
              <p className="text-gray-600 text-sm">
                Many common cleaning chemicals are highly toxic to pets who walk on cleaned floors and groom themselves.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-forest relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-forest-light/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-citrus/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

        <div className="relative max-w-3xl mx-auto text-center text-white">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Make the Switch Today
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
            Join thousands of families who have chosen a cleaner, safer, and greener way to clean their homes.
          </p>
          <Link to="/">
            <Button 
              className="bg-white hover:bg-sage text-forest rounded-full px-8 py-6 text-lg font-semibold btn-primary"
              data-testid="cta-explore"
            >
              Explore Our Products
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-forest-dark py-8 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_3cbc8c81-fb19-4dcb-8665-22a7d3f951d4/artifacts/tql0rd5s_6cf00645-7ffc-4b87-89b5-7aecf16c3cb9.jpeg" 
            alt="Plantora Logo" 
            className="h-10 w-auto object-contain bg-white rounded-lg p-1"
          />
          <p className="text-white/60 text-sm text-center">
            Nature's Power in Every Drop
            <br />
            <span className="text-white/40 text-xs">A quality product from Sawariya Global Exports LLP</span>
          </p>
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Plantora
          </p>
        </div>
      </footer>
    </main>
  );
}
